/**
 * Graphics quality.
 *
 * Three explicit levels plus automatic selection. Auto picks a starting level
 * from what the device reports, then adjusts once it has seen real frame times —
 * device sniffing alone is unreliable, and a phone that reports 8 cores may
 * still be thermally throttled.
 */

export type QualityLevel = 'low' | 'medium' | 'high';

export interface QualitySettings {
  level: QualityLevel;
  /** Multiplier on the backbuffer. Below 1 renders fewer pixels and upscales. */
  renderScale: number;
  shadows: boolean;
  shadowMapSize: number;
  /** Metres past which props stop drawing. */
  propDrawDistance: number;
  /** Metres past which buildings stop drawing. */
  buildingDrawDistance: number;
  particles: boolean;
  /** Video advertising is replaced with the still image below this level. */
  videoAds: boolean;
  antialias: boolean;
  maxLights: number;
}

export const QUALITY: Readonly<Record<QualityLevel, QualitySettings>> = {
  low: {
    level: 'low',
    renderScale: 0.7,
    shadows: false,
    shadowMapSize: 0,
    propDrawDistance: 55,
    buildingDrawDistance: 110,
    particles: false,
    videoAds: false,
    antialias: false,
    maxLights: 2
  },
  medium: {
    level: 'medium',
    renderScale: 0.9,
    shadows: true,
    shadowMapSize: 1024,
    propDrawDistance: 90,
    buildingDrawDistance: 170,
    particles: true,
    videoAds: true,
    antialias: true,
    maxLights: 4
  },
  high: {
    level: 'high',
    renderScale: 1,
    shadows: true,
    shadowMapSize: 2048,
    propDrawDistance: 150,
    buildingDrawDistance: 260,
    particles: true,
    videoAds: true,
    antialias: true,
    maxLights: 6
  }
};

export interface DeviceHints {
  isTouch: boolean;
  hardwareConcurrency: number;
  deviceMemoryGb: number | undefined;
  devicePixelRatio: number;
  /** navigator.connection.saveData, when the browser exposes it. */
  saveData: boolean;
}

export function readDeviceHints(win: Window = window): DeviceHints {
  const nav = win.navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };
  return {
    isTouch: (win.matchMedia?.('(pointer: coarse)').matches ?? false) &&
      ('ontouchstart' in win || (nav.maxTouchPoints ?? 0) > 0),
    hardwareConcurrency: nav.hardwareConcurrency ?? 4,
    deviceMemoryGb: nav.deviceMemory,
    devicePixelRatio: win.devicePixelRatio ?? 1,
    saveData: nav.connection?.saveData ?? false
  };
}

/** Initial guess before any frame has been drawn. */
export function chooseInitialQuality(hints: DeviceHints): QualityLevel {
  // Data saver is an explicit request from the person, so it wins outright.
  if (hints.saveData) return 'low';

  const memory = hints.deviceMemoryGb;
  if (memory !== undefined && memory <= 2) return 'low';
  if (hints.hardwareConcurrency <= 2) return 'low';

  if (hints.isTouch) {
    // Phones: be conservative. Dropping frames reads as a broken game; a
    // slightly softer image does not.
    if (memory !== undefined && memory >= 6 && hints.hardwareConcurrency >= 8) return 'medium';
    return hints.hardwareConcurrency >= 6 ? 'medium' : 'low';
  }

  if (hints.hardwareConcurrency >= 8) return 'high';
  return 'medium';
}

const ORDER: readonly QualityLevel[] = ['low', 'medium', 'high'];

/**
 * Adjusts quality from measured frame times.
 *
 * Deliberately asymmetric: drops after a short spell of bad frames, but only
 * raises after a long stretch of good ones. Otherwise a device sitting near the
 * boundary oscillates, and flicking between quality levels looks worse than
 * simply staying on the lower one.
 */
export function adjustQuality(
  current: QualityLevel,
  averageFps: number,
  secondsAtThisLevel: number
): QualityLevel {
  const index = ORDER.indexOf(current);

  if (averageFps < 26 && secondsAtThisLevel > 4 && index > 0) {
    return ORDER[index - 1] as QualityLevel;
  }
  if (averageFps > 57 && secondsAtThisLevel > 20 && index < ORDER.length - 1) {
    return ORDER[index + 1] as QualityLevel;
  }
  return current;
}

/** Rolling average of the last N frame times, in frames per second. */
export class FpsMeter {
  private readonly samples: number[] = [];
  private readonly capacity: number;

  constructor(capacity = 60) {
    this.capacity = capacity;
  }

  record(deltaSeconds: number): void {
    if (deltaSeconds <= 0) return;
    this.samples.push(deltaSeconds);
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  get fps(): number {
    if (this.samples.length === 0) return 0;
    const total = this.samples.reduce((a, b) => a + b, 0);
    return this.samples.length / total;
  }

  reset(): void {
    this.samples.length = 0;
  }
}
